import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { EditableText } from '../components/EditableText';
import { ImageDropZone } from '../components/ImageDropZone';

export interface SocialLink {
  platform: string;
  url: string;
}

export interface FooterContent {
  socialLinks: SocialLink[];
  contactInfo: {
    phone: string;
    email: string;
    address: string;
  };
  navigationLinks: Array<{ label: string; url: string }>;
  copyrightText: string;
  brokerageLogo?: string; // Brokerage logo for footer
  brokerageName?: string; // Brokerage name for footer
}

interface FooterSectionProps {
  content: FooterContent;
  onUpdate: (updates: Partial<FooterContent>) => void;
  organizationId: string;
  isPreview: boolean;
  accentColor?: string;
  onBrokerageLogoUpload?: (file: File) => void;
}

export const FooterSection: React.FC<FooterSectionProps> = ({
  content,
  onUpdate,
  organizationId,
  isPreview,
  accentColor,
  onBrokerageLogoUpload,
}) => {
  const addSocialLink = () => {
    const newLink: SocialLink = {
      platform: '[Social Platform]',
      url: '[URL]',
    };
    onUpdate({ socialLinks: [...content.socialLinks, newLink] });
  };

  const removeSocialLink = (index: number) => {
    const updatedLinks = content.socialLinks.filter((_, i) => i !== index);
    onUpdate({ socialLinks: updatedLinks });
  };

  const updateSocialLink = (index: number, field: keyof SocialLink, value: string) => {
    const updatedLinks = [...content.socialLinks];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    onUpdate({ socialLinks: updatedLinks });
  };

  const updateContactInfo = (field: keyof FooterContent['contactInfo'], value: string) => {
    onUpdate({
      contactInfo: {
        ...content.contactInfo,
        [field]: value,
      },
    });
  };

  return (
    <footer className="w-full bg-zinc-950 text-white py-12">
      <div className="max-w-7xl mx-auto px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-8">
          {/* Column 1: Social Links */}
          <div>
            <h3
              className="font-bold text-lg mb-4 uppercase"
              style={{ color: accentColor || '#FACC15' }}
            >
              Follow Us
            </h3>
            <div className="space-y-3">
              {content.socialLinks.map((link, index) => (
                <div key={index} className="flex items-center gap-2 relative group">
                  <div className="flex-1 text-gray-300">
                    <EditableText
                      value={link.platform}
                      onChange={(value) => updateSocialLink(index, 'platform', value)}
                      isPreview={isPreview}
                      iconColor={accentColor}
                    />
                    {!isPreview && (
                      <div className="text-xs text-gray-500 mt-1">
                        <EditableText
                          value={link.url}
                          onChange={(value) => updateSocialLink(index, 'url', value)}
                          isPreview={isPreview}
                          iconColor={accentColor}
                        />
                      </div>
                    )}
                  </div>
                  {!isPreview && content.socialLinks.length > 1 && (
                    <button
                      onClick={() => removeSocialLink(index)}
                      className="p-1 bg-red-500 hover:bg-red-600 text-white rounded transition-colors opacity-0 group-hover:opacity-100"
                      title="Remove social link"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
              {!isPreview && (
                <button
                  onClick={addSocialLink}
                  className="flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-80"
                  style={{ color: accentColor || '#FACC15' }}
                >
                  <Plus className="w-4 h-4" />
                  Add Social Link
                </button>
              )}
            </div>
          </div>

          {/* Column 2: Contact Info */}
          <div>
            <h3
              className="font-bold text-lg mb-4 uppercase"
              style={{ color: accentColor || '#FACC15' }}
            >
              Contact
            </h3>
            <div className="space-y-3 text-gray-300">
              <div>
                <div className="text-sm text-gray-500 mb-1">Phone:</div>
                <EditableText
                  value={content.contactInfo.phone}
                  onChange={(value) => updateContactInfo('phone', value)}
                  isPreview={isPreview}
                  iconColor={accentColor}
                />
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Email:</div>
                <EditableText
                  value={content.contactInfo.email}
                  onChange={(value) => updateContactInfo('email', value)}
                  isPreview={isPreview}
                  iconColor={accentColor}
                />
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Address:</div>
                <EditableText
                  value={content.contactInfo.address}
                  onChange={(value) => updateContactInfo('address', value)}
                  isPreview={isPreview}
                  multiline
                  iconColor={accentColor}
                />
              </div>
            </div>
          </div>

          {/* Column 3: Quick Links */}
          <div>
            <h3
              className="font-bold text-lg mb-4 uppercase"
              style={{ color: accentColor || '#FACC15' }}
            >
              Quick Links
            </h3>
            <div className="space-y-2">
              {content.navigationLinks.map((link, index) => (
                <div
                  key={index}
                  className="text-gray-300 transition-colors cursor-pointer"
                  style={{
                    ['--hover-color' as any]: accentColor || '#FACC15',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = accentColor || '#FACC15';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '';
                  }}
                >
                  {link.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Brokerage Info Section */}
        <div className="border-t border-gray-700 pt-8 mb-6">
          <div className="flex flex-col items-center gap-4">
            {/* Brokerage Logo - Same as HeroSection logo */}
            <div className="flex-shrink-0">
              {content.brokerageLogo ? (
                <div className="relative group">
                  <div className="w-24 h-24 flex items-center justify-center">
                    <img
                      src={content.brokerageLogo}
                      alt="Brokerage Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  {!isPreview && (
                    <div className="absolute inset-0">
                      <ImageDropZone
                        onImageDrop={(file) => {
                          if (onBrokerageLogoUpload) {
                            onBrokerageLogoUpload(file);
                          }
                        }}
                        isUploading={false}
                        label="Drop new logo"
                        compact
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-24 h-24 flex items-center justify-center relative">
                  <span className="text-white font-bold text-sm">[BROKERAGE LOGO]</span>
                  {!isPreview && (
                    <ImageDropZone
                      onImageDrop={(file) => {
                        if (onBrokerageLogoUpload) {
                          onBrokerageLogoUpload(file);
                        }
                      }}
                      isUploading={false}
                      label="Drop logo"
                      showAlways
                      compact
                    />
                  )}
                </div>
              )}
            </div>

            {/* Brokerage Name */}
            <div className="text-gray-400 text-sm">
              <EditableText
                value={content.brokerageName || '[Brokerage Name]'}
                onChange={(value) => onUpdate({ brokerageName: value })}
                isPreview={isPreview}
                iconColor={accentColor}
              />
            </div>
          </div>
        </div>

        {/* Bottom Bar - Copyright */}
        <div className="border-t border-gray-700 pt-8 text-center">
          <div className="text-gray-400 text-sm">
            <EditableText
              value={content.copyrightText}
              onChange={(value) => onUpdate({ copyrightText: value })}
              isPreview={isPreview}
              iconColor={accentColor}
            />
          </div>
        </div>
      </div>
    </footer>
  );
};
